import type { ComponentProps, MouseEvent } from 'react';
import { Link } from 'react-router-dom';

import { useNavigateWithTransition } from '@muzly/shared';

/**
 * `<Link>` that routes through the page-transition hook.
 *
 * A bare `<Link>` navigates through react-router directly, so it never reaches
 * `useNavigateWithTransition` and the platform slide is skipped for that one
 * path. Wrapping the anchor — rather than swapping it for a `<button>` — keeps
 * the href, so screen readers still announce a link, hovering still previews the
 * destination, and open-in-new-tab still works.
 *
 * Only for stack navigation (entering a full-screen route). Tab-internal moves
 * stay plain `<Link>`s: they don't animate.
 */

type TransitionLinkProps = ComponentProps<typeof Link>;

/** A click the browser owns: new tab, new window, download, non-primary button. */
const isBrowserClick = (event: MouseEvent<HTMLAnchorElement>): boolean =>
  event.button !== 0 ||
  event.metaKey ||
  event.ctrlKey ||
  event.shiftKey ||
  event.altKey;

export const TransitionLink = ({
  to,
  onClick,
  ...props
}: TransitionLinkProps) => {
  const navigate = useNavigateWithTransition();

  const handleClick = (event: MouseEvent<HTMLAnchorElement>): void => {
    onClick?.(event);
    if (event.defaultPrevented || isBrowserClick(event)) {
      return;
    }

    event.preventDefault();
    void navigate(to);
  };

  return <Link to={to} onClick={handleClick} {...props} />;
};
